import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export interface SupplierHit {
  supplier_id: string;
  company_name: string;
  ntn_no?: string;
  supplier_code?: string;
}

export interface SupplierSearchPickerProps {
  onPick: (s: SupplierHit) => void;
}

/**
 * Free-text supplier picker for the SUPPLIER_NOT_FOUND bucket. Hits the
 * backend `/api/suppliers/search` proxy (Slice 2 Task 2.5) which searches
 * Oracle suppliers through the configured query gateway. Results are intentionally never
 * auto-promoted: the user clicks a row to populate the reprocess form.
 */
export function SupplierSearchPicker({ onPick }: SupplierSearchPickerProps) {
  const [q, setQ] = useState('');
  const trimmed = q.trim();
  const enabled = trimmed.length >= 2;

  const { data = [], isLoading, isError } = useQuery<SupplierHit[]>({
    queryKey: ['supplier-search', trimmed],
    queryFn: async () => {
      const res = await apiClient.get(
        `/suppliers/search?q=${encodeURIComponent(trimmed)}`,
      );
      const body = res.data;
      return Array.isArray(body) ? (body as SupplierHit[]) : [];
    },
    enabled,
  });

  return (
    <div className="flex flex-col gap-8">
      <input
        type="search"
        placeholder="Search by name or NTN…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="rounded-md border border-neutral-200 bg-neutral-0 px-12 py-8 text-sm focus:border-primary-500 focus:outline-none"
      />
      {!enabled && (
        <p className="text-xs text-neutral-500">
          Type at least 2 characters to search.
        </p>
      )}
      {enabled && isLoading && (
        <p className="text-xs text-neutral-500">Searching…</p>
      )}
      {enabled && isError && (
        <p className="text-xs text-error-600">Search failed. Try again.</p>
      )}
      {enabled && !isLoading && !isError && data.length === 0 && (
        <p className="text-xs text-neutral-500">No matches.</p>
      )}
      {data.length > 0 && (
        <ul className="max-h-64 overflow-y-auto rounded-md border border-neutral-200 divide-y divide-neutral-100">
          {data.map((s) => (
            <li key={`${s.supplier_id}-${s.ntn_no ?? ''}`}>
              <button
                type="button"
                onClick={() => onPick(s)}
                className="block w-full px-12 py-8 text-left text-sm hover:bg-neutral-50"
              >
                <span className="font-medium text-neutral-900">
                  {s.company_name}
                </span>
                <span className="ml-8 text-xs text-neutral-500">
                  NTN {s.ntn_no ?? '—'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
